// Markets as a Cycle Clock (DWLF-301): where each market sits between its last confirmed weekly low
// and the next one (or, in Highs mode, DWLF-552, its last weekly high and the next one), drawn from the STORED window (GET /cycles/windows) and the screener stage
// (GET /screener/cycle-setups). Nothing here detects or scores a cycle — it places stored, dated
// edges on a dial, which the API documents as the client's job ("compare today with the dated edges").
//
// Position p: 0 = the last confirmed pivot, low or high (the window's ruler), 1 = the window centre (the typical
// gap). The window opens near p≈0.9 and closes near p≈1.1 for a regular cycle.

import { cycleWindowPhase, inEarlyZone } from '../cycleWindowBand';

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;
// gaps.spreadPct (from the API) at which a dot reaches the inner ring; tighter spreads sit further out.
const SPREAD_FLOOR_PCT = 150;

// `attention`: the window is open or past it, so the sidebar names the stage and the dial labels the dot.
export const STAGES = {
  confirmed_setup: { key: 'confirmed', label: 'Confirmed setup', token: 'accent', attention: true },
  awaiting_confirmation: { key: 'awaiting', label: 'Awaiting confirmation', token: 'warn', attention: true },
  early_window: { key: 'early', label: 'Early window', token: 'warn-40' },
  window_open: { key: 'open', label: 'Window open', token: 'warn', attention: true },
  overdue: { key: 'overdue', label: 'Overdue', token: 'down', attention: true },
  missed: { key: 'missed', label: 'Missed', token: 'down', attention: true },
  in_cycle: { key: 'cycle', label: 'In cycle', token: 'ink-85' },
  none: { key: 'none', label: 'No reliable cycle', token: 'ink-40' },
  error: { key: 'error', label: "Couldn't load", token: 'ink-40' },
};

// App URL form, also the join key between the watchlist, the windows and the screener rows.
export const symbolKey = (s) => String(s || '').toUpperCase().replace('/', '-');

// The window's depth band (DWLF-416), when the windows were read with depth=1.
const depthOf = (cycleWindow) => cycleWindow?.depth ?? null;

const fraction = (edge, rulerTime, span) => (edge && Number.isFinite(edge.time) ? (edge.time - rulerTime) / span : null);

// Stage: a confirmed setup on the screener reads as confirmed. Otherwise a window the dates say has closed
// (`cycleWindowPhase`, no live pivot) reads as Overdue or Missed, even with an open or awaiting screener row;
// before that the screener's row wins, then the dates: open, early zone (`inEarlyZone`), or in cycle.
function stageOf(current, setupRow, nowMs) {
  const screener = setupRow?.stage && STAGES[setupRow.stage];
  if (screener === STAGES.confirmed_setup) return screener;
  const phase = cycleWindowPhase({ cycleWindow: current, now: nowMs });
  if (phase === 'overdue') return STAGES.overdue;
  if (phase === 'missed') return STAGES.missed;
  if (screener) return screener;
  if (phase === 'open') return STAGES.window_open;
  if (inEarlyZone({ cycleWindow: current, now: nowMs })) return STAGES.early_window;
  return STAGES.in_cycle;
}

/**
 * @param {string} symbol         watchlist symbol (URL form, e.g. XAU-USD)
 * @param {object|null} windows   GET /cycles/windows response for timeframe 1w
 * @param {object|null} setupRow  the symbol's row in /screener/cycle-setups `long` (lows) or `short` (highs), if any
 * @param {number} nowMs
 * @param {{failed?: boolean, side?: 'low'|'high'}} [opts]  failed: the windows request failed; side: which
 *   pivot's window to place (DWLF-552), lows by default
 */
export function buildClockMarket(symbol, windows, setupRow, nowMs, { failed = false, side = 'low' } = {}) {
  const current = windows?.[side]?.current || null;
  const base = { symbol: symbolKey(symbol), side, setup: setupRow || null, window: current, depth: depthOf(current), verification: windows?.verification || null };
  // A request that failed says nothing about the market — never let it read as "no reliable cycle".
  if (failed) return { ...base, onDial: false, stage: STAGES.error };

  const rulerTime = current?.ruler?.pivotTime;
  const centreTime = current?.centre?.time;
  if (!current || current.calibration !== 'calibrated' || !Number.isFinite(rulerTime) || !Number.isFinite(centreTime) || centreTime <= rulerTime) {
    return { ...base, onDial: false, stage: STAGES.none };
  }

  const span = centreTime - rulerTime;
  const p = (nowMs - rulerTime) / span;
  const gaps = current.gaps || {};
  // Radius: the API's own spread of this market's past gaps (gaps.spreadPct) — tighter sits further out.
  // Geometry for a number the API ships, not a new metric.
  const spreadPct = Number.isFinite(gaps.spreadPct) ? gaps.spreadPct : null;
  const radius = spreadPct == null ? 0 : 1 - Math.min(spreadPct, SPREAD_FLOOR_PCT) / SPREAD_FLOOR_PCT;

  const stage = stageOf(current, setupRow, nowMs);

  return {
    ...base,
    onDial: true,
    p,
    radius,
    spreadPct,
    stage,
    weeksSincePivot: (nowMs - rulerTime) / WEEK_MS,
    typicalWeeks: gaps.q50Bars ?? null,
    gapCount: gaps.n ?? null,
    arcs: {
      earliest: fraction(current.earliest, rulerTime, span),
      opens: fraction(current.opens, rulerTime, span),
      closes: fraction(current.closes, rulerTime, span),
      hardMax: fraction(current.hardMax, rulerTime, span),
    },
  };
}

// One lap of the dial = LAP typical gaps, so a market past its typical gap keeps moving round the
// overdue side instead of wrapping back to look like a fresh cycle. The window centre (p = 1) sits at
// the bottom; the last confirmed pivot (p = 0) at the seam, upper left; a market that has run a whole
// lap without a confirmed pivot is pinned just before the seam.
export const LAP = 1.5;
export const dialPosition = (m) => (m.onDial ? Math.max(0, Math.min(m.p, LAP - 0.02)) : null);

// SVG angle (clockwise from the positive x axis): p = 1 at 90° (bottom).
export const angleOf = (p) => ((90 + ((p - 1) * 360) / LAP) * Math.PI) / 180;
export const pointAt = (cx, cy, p, r) => [cx + r * Math.cos(angleOf(p)), cy + r * Math.sin(angleOf(p))];

export function sectorPath(cx, cy, a0, a1, r1, r2) {
  const [ox0, oy0] = pointAt(cx, cy, a0, r2);
  const [ox1, oy1] = pointAt(cx, cy, a1, r2);
  const [ix1, iy1] = pointAt(cx, cy, a1, r1);
  const [ix0, iy0] = pointAt(cx, cy, a0, r1);
  const large = Math.abs(a1 - a0) / LAP > 0.5 ? 1 : 0;
  return `M${ox0} ${oy0} A${r2} ${r2} 0 ${large} 1 ${ox1} ${oy1} L${ix1} ${iy1} A${r1} ${r1} 0 ${large} 0 ${ix0} ${iy0} Z`;
}
